import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  humanizeEntityRef,
} from '@backstage/plugin-catalog-react';
import { TextField } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useCallback } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { FieldValidation } from '@rjsf/core';

/**
 * The input props that can be specified under `ui:options` for the
 * `EntityPicker` field extension.
 *
 * @public
 */
export interface TerraformRepoPickerUiOptions {
}

/**
 * The underlying component that is rendered in the form for the `EntityPicker`
 * field extension.
 *
 * @public
 */
export const TerraformRepoPicker = (
  props: FieldExtensionComponentProps<string, TerraformRepoPickerUiOptions>,
) => {
  const {
    onChange,
    schema: { title = 'Repository', description = 'A terraform repository from the catalog' },
    required,
    rawErrors,
    formData,
    idSchema,
  } = props;

  const defaultKind = 'component';
  const defaultNamespace = undefined;

  const catalogApi = useApi(catalogApiRef);

  const { value: entities, loading } = useAsync(() =>
    catalogApi.getEntities({
      filter: {
        kind: "Component",
        "spec.type": "terraform-repository",
      }
    })
  );

  const entityRefs = entities?.items.map(e => ({
    label: humanizeEntityRef(e, { defaultKind, defaultNamespace }),
    url: e.metadata.annotations?.repoUrl || 'no-url'
  }));

  const onSelect = useCallback(
    (_: any, value: { label: string; url: string; } | null) => {
      onChange(value?.url ?? undefined);
    },
    [onChange],
  );

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      <Autocomplete
        id={idSchema?.$id}
        getOptionLabel={(option) => option.label}
        loading={loading}
        onChange={onSelect}
        options={entityRefs || []}
        autoSelect
        freeSolo={false}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            margin="dense"
            helperText={description}
            FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
            variant="outlined"
            required={required}
            InputProps={params.InputProps}
          />
        )}
      />
    </FormControl>
  );
};

export const terraformRepoPickerValidation = (
  value: string,
  validation: FieldValidation,
) => {
  if (value === 'Banana') {
    validation.addError(
      'No bananas allowed.',
    );
  }
};